<template>
    <div>
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-3xl font-bold text-gray-900 mb-2">
                    Area Admins
                </h2>
                <p>
                    Subject area administrators
                </p>
            </div>
            <div>
                <div class="flex items-center gap-3">
                    <!-- Search Filter -->
                    <div class="dropdown dropdown-end">
                        <d-btn flat size="sm">
                            <d-icon icon="filter" />
                        </d-btn>
                        <div tabindex="0" class="dropdown-content z-[1] card card-compact w-64 p-4 shadow bg-white">
                            <d-field label="Search by Name">
                                <d-input 
                                    v-model="filter" 
                                    placeholder="Type to search..."
                                    size="sm"
                                />
                            </d-field>
                        </div>
                    </div>
                    
                    <!-- Add User Button -->
                    <d-btn 
                        color="secondary"
                        @click="openUserModal()"
                    >
                        <d-icon icon="plus" />
                        Add User
                    </d-btn>
                </div>
            </div>
        </div>
        <d-table 
            class="bg-white shadow mt-10 mb-5" 
            :data="areaAdmins" 
            :columns="columns"
            :loading="loading" 
            backend-search 
            backend-sorting 
            :default-sort="[pagination.sortField, pagination.sortOrder]" 
            striped 
            default-sort-direction="desc" 
            @sort="onSort"
        >
            <template v-slot="{ row }">
                <td class="font-medium">{{ row.firstName }}</td>
                <td class="font-medium">{{ row.lastName }}</td>
                <td>{{ row.email }}</td>
                <td>{{ row.phoneNumber || '-' }}</td>
                <td>
                    <d-btn 
                        flat
                        size="sm"
                        @click="launchSubjectAreasModal(row)"
                    >
                        Show Subject Areas
                    </d-btn>
                </td>
                <td>
                    <div class="flex gap-2">
                        <d-btn 
                            flat
                            size="sm"
                            @click="openUserModal(row)"
                        >
                            <d-icon icon="edit" />
                        </d-btn>
                        <d-btn 
                            flat
                            size="sm"
                            color="error"
                            @click="showDeletePrompt(row)"
                        >
                            <d-icon icon="trash" />
                        </d-btn>
                    </div>
                </td>
            </template>
        </d-table>

        <!-- Pagination -->
        <div class="flex justify-end mt-6">
            <d-pagination 
                :total-items="total" 
                :per-page="pagination.pageSize" 
                v-model:current="pagination.page" 
            />
        </div>

        <!-- User Modal -->
        <user-modal
            :active="userModalActive"
            :user="selectedUser"
            @close="closeUserModal"
            @saved="onUserSaved"
        />

        <!-- Delete Confirmation Modal -->
        <confirm-modal
            :active="deleteModalActive"
            :title="'Delete User'"
            :message="`Are you sure you want to delete <strong>${userToDelete?.email}</strong>?<br><br>This action cannot be undone.`"
            :confirm-text="'Delete User'"
            @close="closeDeleteModal"
            @confirm="confirmDelete"
        />

        <!-- Subject Areas Modal -->
        <d-modal 
            :active="subjectAreasModalActive"
            :width="600"
            @close="closeSubjectAreasModal"
            :can-cancel="['escape', 'x', 'outside']"
        >
            <user-subject-area-listing-modal
                :user="subjectAreasUser"
                @close="closeSubjectAreasModal"
            />
        </d-modal>
    </div>
</template>

<script>
import axios from 'axios';
import UserModal from './components/UserModal.vue';
import ConfirmModal from './components/ConfirmModal.vue';
import subjectAreasModal from './components/UserSubjectAreaListingModal.vue';
import { toast } from '@services/toast.service';
import { debounce } from '@utils/debounce';

export default {
  name: 'AreaAdminListingPage',
  
  components: {
    UserModal,
    ConfirmModal,
    subjectAreasModal,
  },

  data() {
    return {
      areaAdmins: [],
      loading: false,
      filter: '',
      total: 0,
      userModalActive: false,
      selectedUser: null,
      deleteModalActive: false,
      userToDelete: null,
      subjectAreasModalActive: false,
      subjectAreasUser: null,
      pagination: {
        page: 1,
        pageSize: 20,
        sortField: 'firstName',
        sortOrder: 'asc',
      },
    };
  },

  computed: {
    columns() {
      return [
        { field: 'firstName', label: 'First Name', sortable: true },
        { field: 'lastName', label: 'Last Name', sortable: true },
        { field: 'email', label: 'Email', sortable: true },
        { field: 'phoneNumber', label: 'Phone #', sortable: true },
        { field: 'subjectAreaDetails', label: 'Subject Areas', sortable: false },
        { field: 'actions', label: 'Actions', sortable: false },
      ];
    },
  },

  watch: {
    pagination: {
      handler() {
        this.getUsersPaged();
      },
      deep: true,
    },
    filter() {
      this.pagination.page = 1; // Reset to first page when filtering
      this.debouncedSearch();
    },
  },

  created() {
    // Create debounced search function with 400ms delay
    this.debouncedSearch = debounce(() => {
      this.getUsersPaged();
    }, 400);
  },

  mounted() {
    this.getUsersPaged();
  },

  methods: {
    async getUsersPaged() {
      const { page, sortField, sortOrder, pageSize } = this.pagination;
      const params = [
        `filter=${encodeURIComponent(this.filter)}`,
        `sortDir=${sortField} ${sortOrder}`,
        `currentPage=${page}`,
        `pageSize=${pageSize}`,
        'roleName=Area Admin'
      ].join('&');
      
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/users/paged?${params}`);
        this.areaAdmins = data.resultItems || [];
        this.total = data.totalRecords || 0;
      } catch (error) {
        console.error('Error loading users:', error);
        toast.error('Failed to load users');
        this.areaAdmins = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    openUserModal(user = null) {
      this.selectedUser = user;
      this.userModalActive = true;
    },

    closeUserModal() {
      this.userModalActive = false;
      this.selectedUser = null;
    },

    onUserSaved() {
      this.getUsersPaged();
      this.closeUserModal();
    },

    launchSubjectAreasModal(user) {
      this.subjectAreasUser = user;
      this.subjectAreasModalActive = true;
    },

    closeSubjectAreasModal() {
      this.subjectAreasModalActive = false;
      this.subjectAreasUser = null;
    },

    showDeletePrompt(user) {
      this.userToDelete = user;
      this.deleteModalActive = true;
    },

    closeDeleteModal() {
      this.deleteModalActive = false;
      this.userToDelete = null;
    },

    async confirmDelete() {
      if (!this.userToDelete) return;
      
      try {
        await axios.delete(`/api/users/${this.userToDelete.id}`);
        await this.getUsersPaged();
        toast.success('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      } finally {
        this.closeDeleteModal();
      }
    },

    onSort(field, order) {
      this.pagination.sortField = field;
      this.pagination.sortOrder = order;
    },
  },
};
</script>